import React, { FC, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { trackEvent } from 'ui/js/helpers/tracking';
import { WebElement } from 'ui/types/common';
import styles from './index.module.css';

interface Props extends WebElement {
  triggerOnce?: boolean;
  trackOpacity?: boolean;
  flex?: boolean;
  children: JSX.Element;
}

function isElementOpaque(el: HTMLElement | Element): boolean {
  // Is the element's own opacity greater than 0.5? 
  const isOpaque = Number(getComputedStyle(el).opacity) > 0.5;

  // If it is not, return false.
  if (!isOpaque) return false;

  // If everything so far is opaque, and there are no more elements in the parent chain, return true.
  if (!el.parentElement) return true;

  // Check parent element.
  return isElementOpaque(el.parentElement);
}

const TrackElementViewed: FC<Props> = ( { triggerOnce = false, trackOpacity = false, flex = false, children, ...webElement  } ) => {
  const { ref, inView, entry } = useInView({ threshold: 0.7 });
  const [ hasTracked, setHasTracked ] = useState(false);
  const [ isOpaque, setIsOpaque ] = useState(false);

  // Setup opacity tracking logic if needed. In most simple cases this is not necessary.
  if (trackOpacity) {

    // When there's a style change, recheck opacity and update component state.
    const handleStyleChange = () => {
      if (!entry?.target) return;
      const isOpaque = isElementOpaque(entry.target);

      setIsOpaque(isOpaque);
    }
    
    // Setup mutation observer to track style changes.
    const mutationObserver = new MutationObserver(handleStyleChange);
    const initStyleObserver = (el: HTMLElement | Element) => {
      mutationObserver.observe(el, {
        attributes: true,
        attributeFilter: ['style', 'class']
      });

      // Observe the full parent chain.
      if (el.parentElement) initStyleObserver(el.parentElement);
    }

    // Set initial isOpaque state and connect the mutation observers.
    useEffect(() => {
      if (entry?.target) {
        if (inView) initStyleObserver(entry.target);
        setIsOpaque(isElementOpaque(entry.target));
      }

      // Cleanup mutation observer on unmount.
      return () => {
        mutationObserver.disconnect();
      }
    }, [inView, entry]);
  }
  
  // Watch for changes to component state.
  useEffect(() => {
    if (!inView) return;
    if (trackOpacity && !isOpaque) return;
    if (triggerOnce && hasTracked) return;
  
    // Fire tracking event.
    trackEvent({ action: 'elementViewed', data: webElement });
    setHasTracked(true);
  }, [inView, isOpaque])

  return (
    <div ref={ ref } className={ flex ? styles.flex : '' }>
      {children}
    </div>
  );
};

TrackElementViewed.displayName = 'TrackElementViewed';

export default TrackElementViewed;
